import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import RichTextEditor from "@/components/app/RichTextEditor";
import { stripHtml } from "@/components/app/RichTextView";
import DropZone from "@/components/app/DropZone";
import {
  LogOut,
  Shield,
  Users,
  PlusCircle,
  Trash2,
  KeyRound,
  ChevronDown,
  ChevronRight,
  Upload,
  Save,
  History,
  ArrowLeft,
  Clock,
  FileText,
  X,
  GripVertical,
} from "lucide-react";
const PAGES = [
  { id: "home", label: "Homepage hero image" },
  { id: "important_messages", label: "Important Messages" },
  { id: "faq_residents", label: "FAQs for Residents (PDF)" },
  { id: "faq_leaseholders", label: "FAQs for Leaseholders (PDF)" },
  { id: "rta", label: "Your RTA (PDF)" },
  { id: "news", label: "Latest News" },
  { id: "projects", label: "Building Projects" },
  { id: "history", label: "History" },
  { id: "contact", label: "Managing Agent Contact" },
  { id: "rules", label: "Rules of the Building" },
  { id: "admins", label: "Administrators (Super Admin only)" },
];

export default function AdminDashboard() {
  const { logout, adminEmail, adminName, isSuper } = useAuth();
  const nav = useNavigate();
  const [page, setPage] = useState("home");
  const [activity, setActivity] = useState([]);
  const [showAll, setShowAll] = useState(false);

  const refreshActivity = async () => {
    const { data } = await api.get("/activity-log", { params: { limit: 100 } });
    setActivity(data || []);
  };

  useEffect(() => {
    refreshActivity();
  }, []);

  const doLogout = () => {
    logout();
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="border-b border-stone-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <Link to="/" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900">
              <ArrowLeft className="h-4 w-4" /> Site
            </Link>
            <div className="h-5 w-px bg-stone-300" />
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-amber-800" />
              <span className="font-serif text-xl font-semibold text-stone-900">Administration</span>
              {isSuper && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
                  Super Admin
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-sm font-medium text-stone-900">{adminName}</div>
              <div className="text-xs text-stone-500">{adminEmail}</div>
            </div>
            <button
              onClick={doLogout}
              data-testid="admin-logout-button"
              className="rounded-lg p-2 text-stone-500 hover:bg-stone-100 hover:text-stone-900"
              title="Logout"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 py-6 sm:px-6 lg:grid-cols-[280px_1fr] lg:px-8">
        {/* Sidebar / page selector */}
        <aside className="space-y-4">
          <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
            <label className="mb-2 block text-xs font-mono uppercase tracking-widest text-stone-500">
              Edit page
            </label>
            <select
              data-testid="admin-page-selector"
              value={page}
              onChange={(e) => setPage(e.target.value)}
              className="w-full rounded-xl border border-stone-300 bg-white px-3 py-2 outline-none focus:border-amber-700 focus:ring-2 focus:ring-amber-200"
            >
              {PAGES.filter((p) => p.id !== "admins" || isSuper).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>

          <ActivityPanel items={activity} showAll={showAll} setShowAll={setShowAll} />
        </aside>

        <section className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
          {page === "home" && <HomeEditor onSaved={refreshActivity} />}
          {page === "important_messages" && <ImportantMessagesEditor onSaved={refreshActivity} />}
          {page === "faq_residents" && <FaqEditor kind="residents" onSaved={refreshActivity} />}
          {page === "faq_leaseholders" && <FaqEditor kind="leaseholders" onSaved={refreshActivity} />}
          {page === "rta" && <RtaEditor onSaved={refreshActivity} />}
          {page === "news" && <NewsEditor onSaved={refreshActivity} />}
          {page === "projects" && <ProjectsEditor onSaved={refreshActivity} />}
          {page === "history" && <HistoryEditor onSaved={refreshActivity} />}
          {page === "contact" && <ContactEditor onSaved={refreshActivity} />}
          {page === "rules" && <RulesEditor onSaved={refreshActivity} />}
          {page === "admins" && isSuper && <AdminManagement onSaved={refreshActivity} />}
        </section>
      </main>
    </div>
  );
}

/* -------- Activity Panel -------- */
function ActivityPanel({ items, showAll, setShowAll }) {
  const shown = showAll ? items : items.slice(0, 5);
  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-GB");
    } catch {
      return iso;
    }
  };
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 text-stone-500" />
        <h3 className="text-sm font-semibold text-stone-900">Recent activity</h3>
      </div>
      <ul className="mt-3 space-y-2">
        {shown.length === 0 && <li className="text-xs text-stone-500">No activity yet.</li>}
        {shown.map((a, i) => (
          <ActivityEntry key={a.id} entry={a} fmt={fmt} testIndex={i} />
        ))}
      </ul>
      {items.length > 5 && (
        <button
          onClick={() => setShowAll((v) => !v)}
          data-testid="activity-log-expand-button"
          className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-amber-800 hover:text-amber-900"
        >
          <ChevronDown className={`h-3.5 w-3.5 transition ${showAll ? "rotate-180" : ""}`} />
          {showAll ? "Collapse" : `Expand (${items.length - 5} more)`}
        </button>
      )}
    </div>
  );
}

function ActivityEntry({ entry, fmt, testIndex }) {
  const [open, setOpen] = useState(false);
  const hasDiff = entry.diff && Object.keys(entry.diff).length > 0;
  return (
    <li className="rounded-lg bg-stone-50 px-3 py-2 text-xs text-stone-700">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium text-stone-900">{entry.action}</div>
          <div className="truncate text-stone-600">{entry.email}</div>
          {entry.target && (
            <div className="truncate font-mono text-[11px] text-stone-500">{entry.target}</div>
          )}
          <div className="text-stone-500">{fmt(entry.ts)}</div>
        </div>
        {hasDiff && (
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid={`activity-diff-toggle-${testIndex}`}
            className="shrink-0 rounded-md border border-stone-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-stone-700 hover:bg-amber-50 hover:text-amber-900"
            title={open ? "Hide changes" : "Show changes"}
          >
            {open ? "Hide" : "Diff"}
          </button>
        )}
      </div>
      {open && hasDiff && (
        <div className="mt-2 space-y-1.5 border-t border-stone-200 pt-2">
          {Object.entries(entry.diff).map(([field, change]) => (
            <DiffRow key={field} field={field} change={change} />
          ))}
        </div>
      )}
    </li>
  );
}

function DiffRow({ field, change }) {
  const toText = (v) => {
    if (v === null || v === undefined || v === "") return "—";
    if (typeof v === "boolean") return v ? "yes" : "no";
    if (Array.isArray(v)) return v.length ? `${v.length} item(s)` : "—";
    const s = String(v);
    const stripped = s.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    return stripped.length > 80 ? stripped.slice(0, 80) + "…" : stripped;
  };
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-stone-500">{field}</div>
      <div className="flex items-start gap-1.5">
        <span className="rounded bg-red-50 px-1.5 py-0.5 text-[11px] text-red-800 line-through decoration-red-400">
          {toText(change.old)}
        </span>
        <span className="text-stone-400">→</span>
        <span className="rounded bg-green-50 px-1.5 py-0.5 text-[11px] text-green-800">
          {toText(change.new)}
        </span>
      </div>
    </div>
  );
}

/* -------- Home Editor (hero only) -------- */
function HomeEditor({ onSaved }) {
  const [heroPreview, setHeroPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/hero").then((h) => {
      if (h.data?.file_id) setHeroPreview(fileUrl(h.data.file_id));
    });
  }, []);

  const uploadHero = async (files) => {
    const file = files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/hero", form);
      setHeroPreview(fileUrl(data.file_id));
      toast.success("Hero image updated");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <h2 className="font-serif text-2xl font-semibold text-stone-900">Homepage hero image</h2>
      <p className="mt-1 text-sm text-stone-600">
        This full-width image appears above the navigation on the public homepage.
      </p>

      <div className="mt-6 rounded-2xl border border-stone-200 bg-stone-50 p-5">
        {heroPreview && (
          <img src={heroPreview} alt="hero preview" className="mb-4 h-52 w-full rounded-xl object-cover" />
        )}
        <DropZone
          onFiles={uploadHero}
          testid="hero-image-input"
          label={uploading ? "Uploading…" : "Click to upload or drag an image here"}
          disabled={uploading}
        />
      </div>
    </div>
  );
}

/* -------- Important Messages Editor -------- */
function ImportantMessagesEditor({ onSaved }) {
  const [items, setItems] = useState([]);
  const [newText, setNewText] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  const load = async () => {
    const { data } = await api.get("/important-messages");
    setItems(Array.isArray(data) ? data : []);
  };
  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    if (!newText.trim()) return;
    try {
      await api.post("/important-messages", { text: newText });
      setNewText("");
      await load();
      onSaved?.();
      toast.success("Important message posted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const startEdit = (m) => {
    setEditingId(m.id);
    setEditText(m.text);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };
  const saveEdit = async (id) => {
    if (!editText.trim()) return;
    try {
      await api.put(`/important-messages/${id}`, { text: editText });
      toast.success("Message updated");
      cancelEdit();
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this important message?")) return;
    await api.delete(`/important-messages/${id}`);
    await load();
    onSaved?.();
  };

  return (
    <div>
      <h2 className="font-serif text-2xl font-semibold text-stone-900">Important Messages</h2>
      <p className="mt-1 text-sm text-stone-600">
        Messages shown as banners on the homepage. Each one auto-expires 2 months after it's posted.
      </p>

      <div className="mt-5 rounded-2xl border border-stone-200 bg-stone-50 p-5">
        <textarea
          data-testid="important-message-textarea"
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          rows={4}
          className="w-full rounded-xl border border-stone-300 bg-white px-3 py-2 outline-none focus:border-amber-700 focus:ring-2 focus:ring-amber-200"
          placeholder="e.g. Water will be turned off on 12 Oct between 09:00-11:00…"
        />
        <div className="mt-3">
          <button
            onClick={add}
            data-testid="important-message-save-button"
            disabled={!newText.trim()}
            className="inline-flex items-center gap-2 rounded-xl bg-[#c85a32] px-4 py-2 font-medium text-white hover:bg-[#b24e2a] disabled:opacity-50"
          >
            <PlusCircle className="h-4 w-4" /> Post message
          </button>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        <div className="text-xs font-mono uppercase tracking-widest text-stone-500">
          Active messages ({items.length})
        </div>
        {items.map((m, i) => (
          <div key={m.id} className="rounded-2xl border border-stone-200 bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-xs text-stone-500">
                  {new Date(m.created_at).toLocaleString("en-GB")} · {m.author}
                  {m.edited_at && (
                    <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] uppercase tracking-widest text-amber-800">
                      edited {new Date(m.edited_at).toLocaleDateString("en-GB")}
                    </span>
                  )}
                </div>
                {editingId === m.id ? (
                  <textarea
                    data-testid={`important-message-edit-input-${i}`}
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    rows={4}
                    className="mt-2 w-full rounded-xl border border-stone-300 bg-white px-3 py-2"
                  />
                ) : (
                  <p className="mt-1 whitespace-pre-wrap text-stone-800">{m.text}</p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {editingId === m.id ? (
                  <>
                    <button
                      onClick={() => saveEdit(m.id)}
                      data-testid={`important-message-save-edit-${i}`}
                      className="inline-flex items-center gap-1 rounded-lg bg-stone-900 px-2.5 py-1 text-xs font-medium text-white hover:bg-stone-800"
                    >
                      <Save className="h-3.5 w-3.5" /> Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="rounded-lg border border-stone-300 bg-white px-2.5 py-1 text-xs hover:bg-stone-50"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => startEdit(m)}
                      data-testid={`important-message-edit-button-${i}`}
                      className="rounded-lg border border-stone-300 px-2.5 py-1 text-xs hover:bg-stone-50"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => del(m.id)}
                      data-testid={`important-message-delete-button-${i}`}
                      className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1 text-xs text-red-700 hover:bg-red-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-sm text-stone-500">No active important messages.</p>
        )}
      </div>
    </div>
  );
}

/* -------- FAQ Editor -------- */
function FaqEditor({ kind, onSaved }) {
  const [info, setInfo] = useState({});
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/faq/${kind}`);
    setInfo(data || {});
    setUrl(data?.url || "");
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const save = async () => {
    const trimmed = url.trim();
    if (trimmed && !/^https?:\/\//i.test(trimmed)) {
      toast.error("Link must start with http:// or https://");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/faq/${kind}/url`, { url: trimmed });
      toast.success("FAQ link saved");
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const titleFor = (k) => {
    if (k === "residents") return "FAQs for Residents";
    if (k === "leaseholders") return "FAQs for Leaseholders";
    return k;
  };

  return (
    <div>
      <h2 className="font-serif text-2xl font-semibold text-stone-900">{titleFor(kind)}</h2>
      <p className="mt-1 text-sm text-stone-600">
        Paste the Google Drive share link for this FAQ document. The public page will show a button that opens it in a new tab.
      </p>

      <div className="mt-6 space-y-3 rounded-2xl border border-stone-200 bg-stone-50 p-5">
        <label className="block text-sm font-medium text-stone-700" htmlFor={`faq-${kind}-url`}>
          Google Drive link
        </label>
        <input
          id={`faq-${kind}-url`}
          data-testid={`faq-${kind}-url-input`}
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://drive.google.com/..."
          className="w-full rounded-xl border border-stone-300 bg-white px-4 py-2.5 text-sm text-stone-900 outline-none focus:border-amber-700 focus:ring-2 focus:ring-amber-100"
        />
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs text-stone-500">
            {info.updated_at ? `Last updated ${new Date(info.updated_at).toLocaleString("en-GB")}` : "Not set yet"}
          </div>
          <div className="flex items-center gap-2">
            {info.url && (
              <a
                href={info.url}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-medium text-amber-800 hover:text-amber-900"
              >
                Open current
              </a>
            )}
            <button
              onClick={save}
              disabled={saving}
              data-testid={`faq-${kind}-save-url-button`}
              className="inline-flex items-center gap-2 rounded-xl bg-[#78350f] px-4 py-2 font-medium text-white hover:bg-[#602a0c] disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save link"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------- RTA Editor (multiple PDFs, drag to reorder) -------- */
function RtaEditor({ onSaved }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/rta");
    setItems(Array.isArray(data) ? data : []);
  };
  useEffect(() => {
    load();
  }, []);

  const upload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    let added = 0;
    try {
      for (const f of files) {
        if (!f.name.toLowerCase().endsWith(".pdf") && f.type !== "application/pdf") {
          toast.error(`${f.name} is not a PDF — skipped`);
          continue;
        }
        const fm = new FormData();
        fm.append("file", f);
        await api.post("/rta", fm);
        added += 1;
      }
      if (added) toast.success(`${added} document(s) uploaded`);
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this RTA document?")) return;
    await api.delete(`/rta/${id}`);
    await load();
    onSaved?.();
    toast.success("Document removed");
  };

  const persistOrder = async (list) => {
    try {
      await api.post("/rta/reorder", { ids: list.map((x) => x.id) });
      onSaved?.();
      toast.success("Order saved");
    } catch (e) {
      toast.error("Could not save order");
      await load();
    }
  };

  const onDragStart = (id) => (e) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/x-rta-doc", id);
    try { e.dataTransfer.setData("text/plain", id); } catch { /* some browsers need this */ }
  };
  const onDragOver = (id) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverId !== id) setDragOverId(id);
  };
  const onDragLeave = () => setDragOverId(null);
  const onDragEnd = () => {
    setDragId(null);
    setDragOverId(null);
  };
  const onDrop = (targetId) => (e) => {
    e.preventDefault();
    const srcId = e.dataTransfer.getData("application/x-rta-doc") || dragId;
    setDragId(null);
    setDragOverId(null);
    if (!srcId || srcId === targetId) return;
    const srcIdx = items.findIndex((x) => x.id === srcId);
    const tgtIdx = items.findIndex((x) => x.id === targetId);
    if (srcIdx < 0 || tgtIdx < 0) return;
    const reordered = [...items];
    const [moved] = reordered.splice(srcIdx, 1);
    reordered.splice(tgtIdx, 0, moved);
    setItems(reordered);
    persistOrder(reordered);
  };

  return (
    <div>
      <h2 className="font-serif text-2xl font-semibold text-stone-900">Your RTA</h2>
      <p className="mt-1 text-sm text-stone-600">
        Upload one or more PDFs, then drag the <GripVertical className="inline h-3.5 w-3.5 -mt-0.5" /> handle to set the order residents see on the public page (top of this list = top of the public page).
      </p>

      <div className="mt-6 rounded-2xl border border-stone-200 bg-stone-50 p-5">
        <DropZone
          onFiles={upload}
          multiple
          accept="application/pdf,.pdf"
          testid="rta-upload-input"
          label={uploading ? "Uploading…" : "Drag and drop PDFs here, or click to browse"}
          helperText="PDF documents only"
          disabled={uploading}
        />
      </div>

      <div className="mt-6 space-y-3" data-testid="rta-admin-list">
        <div className="text-xs font-mono uppercase tracking-widest text-stone-500">
          Uploaded documents ({items.length})
        </div>
        {items.map((d, i) => (
          <div
            key={d.id}
            draggable
            onDragStart={onDragStart(d.id)}
            onDragOver={onDragOver(d.id)}
            onDragLeave={onDragLeave}
            onDragEnd={onDragEnd}
            onDrop={onDrop(d.id)}
            data-testid={`rta-admin-doc-${i}`}
            data-doc-id={d.id}
            className={`flex items-center gap-3 rounded-2xl border bg-white p-4 transition ${
              dragId === d.id
                ? "border-amber-500 opacity-60"
                : dragOverId === d.id
                ? "border-amber-500 ring-2 ring-amber-200"
                : "border-stone-200 hover:border-stone-300"
            }`}
          >
            <button
              type="button"
              className="shrink-0 cursor-grab touch-none rounded-md p-1 text-stone-400 hover:bg-stone-100 hover:text-stone-700 active:cursor-grabbing"
              title="Drag to reorder"
              data-testid={`rta-admin-drag-handle-${i}`}
              aria-label="Drag to reorder"
            >
              <GripVertical className="h-4 w-4" />
            </button>
            <div className="rounded-lg bg-red-50 p-2 ring-1 ring-red-100">
              <FileText className="h-5 w-5 text-red-700" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium text-stone-900">{d.filename}</div>
              <div className="text-xs text-stone-500">
                Uploaded {new Date(d.created_at).toLocaleString("en-GB")} · {d.uploaded_by}
              </div>
            </div>
            <a
              href={fileUrl(d.file_id)}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-medium text-amber-800 hover:text-amber-900"
            >
              View
            </a>
            <button
              onClick={() => del(d.id)}
              data-testid={`rta-admin-delete-${i}`}
              className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1 text-xs text-red-700 hover:bg-red-100"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-stone-500">No documents uploaded yet.</p>}
      </div>
    </div>
  );
}

/* -------- News Editor -------- */
function NewsEditor({ onSaved }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // obj being edited or null
  const [form, setForm] = useState({ date: "", headline: "", body: "", image_id: null });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/news");
    setItems(data || []);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startNew = () => {
    setEditing({});
    setForm({ date: new Date().toISOString().slice(0, 10), headline: "", body: "", image_id: null, attachments: [] });
  };
  const startEdit = (item) => {
    setEditing(item);
    setForm({
      date: item.date,
      headline: item.headline,
      body: item.body,
      image_id: null,
      attachments: item.attachments || (item.image_id ? [{ file_id: item.image_id, filename: "", content_type: "image/*" }] : []),
    });
  };
  const cancel = () => {
    setEditing(null);
    setForm({ date: "", headline: "", body: "", image_id: null, attachments: [] });
  };

  const uploadFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fm = new FormData();
        fm.append("file", f);
        const { data } = await api.post("/upload-image", fm);
        uploaded.push({
          file_id: data.file_id,
          filename: data.filename || f.name,
          content_type: data.content_type || f.type,
        });
      }
      setForm((prev) => ({ ...prev, attachments: [...(prev.attachments || []), ...uploaded] }));
      toast.success(`${uploaded.length} file(s) uploaded`);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (file_id) =>
    setForm((prev) => ({ ...prev, attachments: prev.attachments.filter((a) => a.file_id !== file_id) }));

  const save = async () => {
    if (!form.headline || !form.body) {
      toast.error("Headline and body required");
      return;
    }
    try {
      if (editing && editing.id) {
        await api.put(`/news/${editing.id}`, form);
        toast.success("Article updated");
      } else {
        await api.post("/news", form);
        toast.success("Article added");
      }
      cancel();
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this news article?")) return;
    await api.delete(`/news/${id}`);
    await load();
    onSaved?.();
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl font-semibold text-stone-900">Latest News</h2>
        {!editing && (
          <button
            onClick={startNew}
            data-testid="news-new-button"
            className="inline-flex items-center gap-2 rounded-xl bg-[#c85a32] px-4 py-2 font-medium text-white hover:bg-[#b24e2a]"
          >
            <PlusCircle className="h-4 w-4" /> New article
          </button>
        )}
      </div>

      {editing && (
        <div className="mt-5 rounded-2xl border border-stone-200 bg-stone-50 p-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[180px_1fr]">
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="rounded-xl border border-stone-300 bg-white px-3 py-2"
            />
            <input
              data-testid="news-headline-input"
              value={form.headline}
              onChange={(e) => setForm({ ...form, headline: e.target.value })}
              placeholder="Headline"
              className="rounded-xl border border-stone-300 bg-white px-3 py-2"
            />
          </div>
          <div className="mt-3">
            <label className="mb-1 block text-xs font-medium text-stone-600">Body</label>
            <RichTextEditor
              testid="news-body-input"
              value={form.body}
              onChange={(html) => setForm({ ...form, body: html })}
              placeholder="Write the article body — use the toolbar to add headings, lists, links…"
            />
          </div>

          <div className="mt-3">
            <label className="mb-1 block text-xs font-medium text-stone-600">Photos &amp; documents (drag &amp; drop)</label>
            <DropZone
              onFiles={uploadFiles}
              multiple
              accept="image/*,application/pdf"
              testid="news-image-input"
              label={uploading ? "Uploading…" : "Drag and drop images or PDFs here, or click to browse"}
              helperText="Images (PNG / JPG / WebP) or PDF documents"
              disabled={uploading}
            />
            {form.attachments?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="news-attachment-preview">
                {form.attachments.map((a) => {
                  const isImg = (a.content_type || "").startsWith("image/");
                  return (
                    <div key={a.file_id} className="relative">
                      {isImg ? (
                        <img src={fileUrl(a.file_id)} alt="" className="h-16 w-24 rounded-lg object-cover" />
                      ) : (
                        <div className="flex h-16 w-40 items-center gap-2 rounded-lg border border-stone-200 bg-white px-2">
                          <FileText className="h-5 w-5 shrink-0 text-red-700" />
                          <div className="min-w-0">
                            <div className="truncate text-xs font-medium text-stone-900">
                              {a.filename || "Document"}
                            </div>
                            <div className="text-[10px] uppercase tracking-widest text-stone-500">PDF</div>
                          </div>
                        </div>
                      )}
                      <button
                        onClick={() => removeAttachment(a.file_id)}
                        className="absolute -right-1.5 -top-1.5 rounded-full bg-stone-900 p-0.5 text-white"
                        type="button"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-4 flex items-center gap-2">
            <button
              onClick={save}
              data-testid="news-save-button"
              className="inline-flex items-center gap-2 rounded-xl bg-stone-900 px-4 py-2 font-medium text-white hover:bg-stone-800"
            >
              <Save className="h-4 w-4" /> Save
            </button>
            <button onClick={cancel} className="rounded-xl border border-stone-300 bg-white px-4 py-2 hover:bg-stone-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 space-y-3">
        {items.map((n, i) => (
          <div key={n.id} className="flex items-start justify-between gap-4 rounded-2xl border border-stone-200 bg-white p-4">
            <div className="min-w-0">
              <div className="text-xs font-mono uppercase tracking-widest text-[#c85a32]">
                {n.date ? new Date(n.date).toLocaleDateString("en-GB") : ""}
              </div>
              <div className="truncate font-serif text-lg font-semibold text-stone-900">{n.headline}</div>
              <div className="truncate text-sm text-stone-600">{stripHtml(n.body).slice(0, 120)}…</div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                onClick={() => startEdit(n)}
                data-testid={`news-edit-button-${i}`}
                className="rounded-lg border border-stone-300 px-3 py-1 text-sm hover:bg-stone-50"
              >
                Edit
              </button>
              <button
                onClick={() => del(n.id)}
                data-testid={`news-delete-button-${i}`}
                className="rounded-lg border border-red-200 bg-red-50 px-3 py-1 text-sm text-red-700 hover:bg-red-100"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-stone-500">No articles yet.</p>}
      </div>
    </div>
  );
}

/* -------- Projects Editor -------- */
function ProjectsEditor({ onSaved }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ date: "", headline: "", body: "", image_ids: [] });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/projects");
    setItems(data || []);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startNew = () => {
    setEditing({});
    setForm({ date: new Date().toISOString().slice(0, 10), headline: "", body: "", image_ids: [], attachments: [] });
  };
  const startEdit = (item) => {
    setEditing(item);
    setForm({
      date: item.date,
      headline: item.headline,
      body: item.body,
      image_ids: item.image_ids || [],
      attachments: item.attachments || [],
    });
  };
  const cancel = () => {
    setEditing(null);
    setForm({ date: "", headline: "", body: "", image_ids: [], attachments: [] });
  };

  const uploadImages = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fm = new FormData();
        fm.append("file", f);
        const { data } = await api.post("/upload-image", fm);
        uploaded.push({
          file_id: data.file_id,
          filename: data.filename || f.name,
          content_type: data.content_type || f.type,
        });
      }
      setForm((prev) => ({ ...prev, attachments: [...(prev.attachments || []), ...uploaded] }));
      toast.success(`${uploaded.length} file(s) uploaded`);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (file_id) =>
    setForm((prev) => ({ ...prev, attachments: prev.attachments.filter((a) => a.file_id !== file_id) }));

  const save = async () => {
    if (!form.headline || !form.body) {
      toast.error("Headline and body required");
      return;
    }
    try {
      if (editing && editing.id) {
        await api.put(`/projects/${editing.id}`, form);
        toast.success("Project updated");
      } else {
        await api.post("/projects", form);
        toast.success("Project added");
      }
      cancel();
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/projects/${id}`);
    await load();
    onSaved?.();
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl font-semibold text-stone-900">Building Projects</h2>
        {!editing && (
          <button
            onClick={startNew}
            data-testid="project-new-button"
            className="inline-flex items-center gap-2 rounded-xl bg-[#b45309] px-4 py-2 font-medium text-white hover:bg-[#92400e]"
          >
            <PlusCircle className="h-4 w-4" /> New project
          </button>
        )}
      </div>

      {editing && (
        <div className="mt-5 rounded-2xl border border-stone-200 bg-stone-50 p-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[180px_1fr]">
            <input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="rounded-xl border border-stone-300 bg-white px-3 py-2"
            />
            <input
              data-testid="project-headline-input"
              value={form.headline}
              onChange={(e) => setForm({ ...form, headline: e.target.value })}
              placeholder="Headline"
              className="rounded-xl border border-stone-300 bg-white px-3 py-2"
            />
          </div>
          <div className="mt-3">
            <label className="mb-1 block text-xs font-medium text-stone-600">Body</label>
            <RichTextEditor
              testid="project-body-input"
              value={form.body}
              onChange={(html) => setForm({ ...form, body: html })}
              placeholder="Describe the project — scope, timeline, impact to residents…"
            />
          </div>

          <div className="mt-3">
            <label className="mb-1 block text-xs font-medium text-stone-600">Photos &amp; documents (drag &amp; drop)</label>
            <DropZone
              onFiles={uploadImages}
              multiple
              accept="image/*,application/pdf"
              testid="project-images-input"
              label={uploading ? "Uploading…" : "Drag and drop images or PDFs here, or click to browse"}
              helperText="Images (PNG / JPG / WebP) or PDF documents"
              disabled={uploading}
            />
            {form.attachments?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="project-attachment-preview">
                {form.attachments.map((a) => {
                  const isImg = (a.content_type || "").startsWith("image/");
                  return (
                    <div key={a.file_id} className="relative">
                      {isImg ? (
                        <img src={fileUrl(a.file_id)} alt="" className="h-16 w-24 rounded-lg object-cover" />
                      ) : (
                        <div className="flex h-16 w-40 items-center gap-2 rounded-lg border border-stone-200 bg-white px-2">
                          <FileText className="h-5 w-5 shrink-0 text-red-700" />
                          <div className="min-w-0">
                            <div className="truncate text-xs font-medium text-stone-900">
                              {a.filename || "Document"}
                            </div>
                            <div className="text-[10px] uppercase tracking-widest text-stone-500">PDF</div>
                          </div>
                        </div>
                      )}
                      <button
                        onClick={() => removeAttachment(a.file_id)}
                        className="absolute -right-1.5 -top-1.5 rounded-full bg-stone-900 p-0.5 text-white"
                        type="button"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-4 flex items-center gap-2">
            <button
              onClick={save}
              data-testid="project-save-button"
              className="inline-flex items-center gap-2 rounded-xl bg-stone-900 px-4 py-2 font-medium text-white hover:bg-stone-800"
            >
              <Save className="h-4 w-4" /> Save
            </button>
            <button onClick={cancel} className="rounded-xl border border-stone-300 bg-white px-4 py-2 hover:bg-stone-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 space-y-3">
        {items.map((n, i) => (
          <div key={n.id} className="flex items-start justify-between gap-4 rounded-2xl border border-stone-200 bg-white p-4">
            <div className="min-w-0">
              <div className="text-xs font-mono uppercase tracking-widest text-[#b45309]">
                {n.date ? new Date(n.date).toLocaleDateString("en-GB") : ""}
              </div>
              <div className="truncate font-serif text-lg font-semibold text-stone-900">{n.headline}</div>
              <div className="truncate text-sm text-stone-600">{stripHtml(n.body).slice(0, 120)}…</div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                onClick={() => startEdit(n)}
                data-testid={`project-edit-button-${i}`}
                className="rounded-lg border border-stone-300 px-3 py-1 text-sm hover:bg-stone-50"
              >
                Edit
              </button>
              <button
                onClick={() => del(n.id)}
                data-testid={`project-delete-button-${i}`}
                className="rounded-lg border border-red-200 bg-red-50 px-3 py-1 text-sm text-red-700 hover:bg-red-100"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-sm text-stone-500">No projects yet.</p>}
      </div>
    </div>
  );
}

/* -------- Contact Editor -------- */
function ContactEditor({ onSaved }) {
  const [form, setForm] = useState({ agent_name: "", email: "", phone: "", address: "" });

  useEffect(() => {
    api.get("/contact").then((r) => setForm(r.data || {}));
  }, []);

  const save = async () => {
    try {
      await api.put("/contact", form);
      toast.success("Contact details saved");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div>
      <h2 className="font-serif text-2xl font-semibold text-stone-900">Managing Agent Contact</h2>
      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Agent name" value={form.agent_name} onChange={(v) => setForm({ ...form, agent_name: v })} testid="contact-agent-input" />
        <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="contact-email-input" />
        <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="contact-phone-input" />
        <Field label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="contact-address-input" />
      </div>
      <div className="mt-5">
        <button
          onClick={save}
          data-testid="contact-save-button"
          className="inline-flex items-center gap-2 rounded-xl bg-stone-900 px-4 py-2 font-medium text-white hover:bg-stone-800"
        >
          <Save className="h-4 w-4" /> Save
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <div className="mb-1 text-xs font-medium text-stone-600">{label}</div>
      <input
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-stone-300 bg-white px-3 py-2 outline-none focus:border-amber-700 focus:ring-2 focus:ring-amber-200"
      />
    </label>
  );
}

/* -------- Rules Editor -------- */
function RulesEditor({ onSaved }) {
  const [rules, setRules] = useState([]);
  const [text, setText] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  const load = async () => {
    const { data } = await api.get("/rules");
    setRules(data || []);
  };
  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    if (!text.trim()) return;
    await api.post("/rules", { text });
    setText("");
    await load();
    onSaved?.();
    toast.success("Rule added");
  };
  const del = async (id) => {
    if (!window.confirm("Delete this rule?")) return;
    await api.delete(`/rules/${id}`);
    await load();
    onSaved?.();
  };
  const startEdit = (r) => {
    setEditingId(r.id);
    setEditText(r.text);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };
  const saveEdit = async (id) => {
    if (!editText.trim()) return;
    try {
      await api.put(`/rules/${id}`, { text: editText });
      toast.success("Rule updated");
      cancelEdit();
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div>
      <h2 className="font-serif text-2xl font-semibold text-stone-900">Rules of the Building</h2>
      <p className="mt-1 text-sm text-stone-600">
        New entries are added to the top of the public list. Prior entries are retained below and can be edited or removed.
      </p>

      <div className="mt-5 rounded-2xl border border-stone-200 bg-stone-50 p-5">
        <textarea
          data-testid="rule-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          className="w-full rounded-xl border border-stone-300 bg-white px-3 py-2"
          placeholder="Add a new building rule or notice…"
        />
        <div className="mt-3">
          <button
            onClick={add}
            data-testid="rule-add-button"
            className="inline-flex items-center gap-2 rounded-xl bg-[#556b2f] px-4 py-2 font-medium text-white hover:bg-[#445625]"
          >
            <PlusCircle className="h-4 w-4" /> Add rule
          </button>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        <div className="text-xs font-mono uppercase tracking-widest text-stone-500">All entries (history)</div>
        {rules.map((r, i) => (
          <div key={r.id} className="rounded-2xl border border-stone-200 bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-xs text-stone-500">
                  {new Date(r.created_at).toLocaleString("en-GB")} · {r.author}
                  {r.edited_at && (
                    <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] uppercase tracking-widest text-amber-800">
                      edited {new Date(r.edited_at).toLocaleDateString("en-GB")}
                    </span>
                  )}
                </div>
                {editingId === r.id ? (
                  <textarea
                    data-testid={`rule-edit-input-${i}`}
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    rows={4}
                    className="mt-2 w-full rounded-xl border border-stone-300 bg-white px-3 py-2"
                  />
                ) : (
                  <p className="mt-1 whitespace-pre-wrap text-stone-800">{r.text}</p>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {editingId === r.id ? (
                  <>
                    <button
                      onClick={() => saveEdit(r.id)}
                      data-testid={`rule-save-button-${i}`}
                      className="inline-flex items-center gap-1 rounded-lg bg-stone-900 px-2.5 py-1 text-xs font-medium text-white hover:bg-stone-800"
                    >
                      <Save className="h-3.5 w-3.5" /> Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="rounded-lg border border-stone-300 bg-white px-2.5 py-1 text-xs hover:bg-stone-50"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => startEdit(r)}
                      data-testid={`rule-edit-button-${i}`}
                      className="rounded-lg border border-stone-300 px-2.5 py-1 text-xs hover:bg-stone-50"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => del(r.id)}
                      data-testid={`rule-delete-button-${i}`}
                      className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1 text-xs text-red-700 hover:bg-red-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
        {rules.length === 0 && <p className="text-sm text-stone-500">No rules yet.</p>}
      </div>
    </div>
  );
}

/* -------- History Editor -------- */
function HistoryEditor({ onSaved }) {
  const [form, setForm] = useState({ title: "", body: "" });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/history").then((r) => {
      setForm({ title: r.data?.title || "", body: r.data?.body || "" });
      setLoaded(true);
    });
  }, []);

  const save = async () => {
    try {
      await api.put("/history", form);
      toast.success("History page saved");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  if (!loaded) return <p className="text-sm text-stone-500">Loading…</p>;

  return (
    <div>
      <h2 className="font-serif text-2xl font-semibold text-stone-900">History</h2>
      <p className="mt-1 text-sm text-stone-600">
        The story of Salford Town Hall. Residents see this on the public History page.
      </p>

      <div className="mt-5 space-y-4">
        <label className="block">
          <div className="mb-1 text-xs font-medium text-stone-600">Page title</div>
          <input
            data-testid="history-title-input"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. The History of Salford Town Hall"
            className="w-full rounded-xl border border-stone-300 bg-white px-3 py-2 outline-none focus:border-amber-700 focus:ring-2 focus:ring-amber-200"
          />
        </label>
        <div>
          <div className="mb-1 text-xs font-medium text-stone-600">Body</div>
          <RichTextEditor
            testid="history-body-input"
            value={form.body}
            onChange={(html) => setForm({ ...form, body: html })}
            placeholder="Tell the story of the building — history, architecture, notable residents…"
          />
        </div>
      </div>

      <div className="mt-5">
        <button
          onClick={save}
          data-testid="history-save-button"
          className="inline-flex items-center gap-2 rounded-xl bg-stone-900 px-4 py-2 font-medium text-white hover:bg-stone-800"
        >
          <Save className="h-4 w-4" /> Save
        </button>
      </div>
    </div>
  );
}

/* -------- Admin Management -------- */
function AdminManagement({ onSaved }) {
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", is_super: false });
  const [lastPin, setLastPin] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admins");
    setAdmins(data || []);
  };
  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    if (!form.email || !form.name) {
      toast.error("Email and name are required");
      return;
    }
    try {
      const { data } = await api.post("/admins", form);
      setLastPin({ email: data.email, pin: data.last_pin });
      toast.success(`Admin added. PIN: ${data.last_pin}`);
      setForm({ email: "", name: "", is_super: false });
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  const resend = async (id) => {
    try {
      const { data } = await api.post(`/admins/${id}/resend-pin`);
      setLastPin({ email: data.email, pin: data.last_pin });
      toast.success(`New PIN for ${data.email}: ${data.last_pin}`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };

  const del = async (id, email) => {
    if (!window.confirm(`Delete admin ${email}?`)) return;
    try {
      await api.delete(`/admins/${id}`);
      toast.success("Admin removed");
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const toggleSuper = async (admin) => {
    try {
      await api.put(`/admins/${admin.id}/super`, { is_super: !admin.is_super });
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div>
      <h2 className="font-serif text-2xl font-semibold text-stone-900">Administrators</h2>
      <p className="mt-1 text-sm text-stone-600">Add admins, reset PINs, delete, or promote to Super Admin.</p>

      {lastPin && (
        <div className="mt-4 rounded-2xl border border-amber-300 bg-amber-50 p-4">
          <div className="text-xs font-mono uppercase tracking-widest text-amber-800">New PIN generated</div>
          <div className="mt-1 text-sm text-stone-800">
            Share with <strong>{lastPin.email}</strong>:{" "}
            <span className="rounded-md bg-white px-2 py-0.5 font-mono text-lg tracking-widest text-amber-900">
              {lastPin.pin}
            </span>
          </div>
          <button onClick={() => setLastPin(null)} className="mt-2 text-xs text-amber-800 hover:underline">
            Dismiss
          </button>
        </div>
      )}

      <div className="mt-5 rounded-2xl border border-stone-200 bg-stone-50 p-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <input
            data-testid="new-admin-email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="Email"
            className="rounded-xl border border-stone-300 bg-white px-3 py-2"
          />
          <input
            data-testid="new-admin-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Name"
            className="rounded-xl border border-stone-300 bg-white px-3 py-2"
          />
          <label className="flex items-center gap-2 text-sm text-stone-700">
            <input
              type="checkbox"
              data-testid="new-admin-super"
              checked={form.is_super}
              onChange={(e) => setForm({ ...form, is_super: e.target.checked })}
              className="h-4 w-4 rounded border-stone-300 text-amber-700 focus:ring-amber-500"
            />
            Make Super Admin
          </label>
        </div>
        <div className="mt-3">
          <button
            onClick={add}
            data-testid="add-admin-button"
            className="inline-flex items-center gap-2 rounded-xl bg-[#c85a32] px-4 py-2 font-medium text-white hover:bg-[#b24e2a]"
          >
            <PlusCircle className="h-4 w-4" /> Add admin
          </button>
        </div>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-stone-200 bg-white" data-testid="super-admin-table">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-left text-xs font-medium uppercase tracking-widest text-stone-500">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-200">
            {admins.map((a, i) => (
              <tr key={a.id}>
                <td className="px-4 py-3 font-medium text-stone-900">{a.name}</td>
                <td className="px-4 py-3 text-stone-700">{a.email}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => toggleSuper(a)}
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      a.is_super
                        ? "bg-amber-100 text-amber-900 hover:bg-amber-200"
                        : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                    }`}
                  >
                    {a.is_super ? "Super Admin" : "Admin"}
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => resend(a.id)}
                    data-testid={`admin-resend-${i}`}
                    className="mr-2 inline-flex items-center gap-1 rounded-lg border border-stone-300 px-2.5 py-1 text-xs hover:bg-stone-50"
                  >
                    <KeyRound className="h-3.5 w-3.5" /> Resend PIN
                  </button>
                  <button
                    onClick={() => del(a.id, a.email)}
                    data-testid={`admin-delete-${i}`}
                    className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1 text-xs text-red-700 hover:bg-red-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
